import { useMemo } from "react";

import { type SpecificationResponse, SpecificationStatus } from "handlers";

import { canShowSaveToCatalogButton } from "entity/specification/common/saveSpecificationToCatalog";

/**
 * Pure: whether view-mode action buttons should be considered for display. False when the viewer
 * is in edit mode, in external/share mode, or looking at a historical snapshot.
 */
export function areActionsVisibleInMode({
    isExternalMode,
    isEditMode,
    selectedSnapshotId,
}: {
    isExternalMode: boolean;
    isEditMode: boolean;
    selectedSnapshotId: string | null;
}) {
    return !isExternalMode && !isEditMode && !selectedSnapshotId;
}

/** Pure: edit button visibility. */
export function canShowEditButton({
    spec,
    canUpdate,
    actionsVisible,
}: {
    spec: SpecificationResponse | undefined;
    canUpdate: boolean;
    actionsVisible: boolean;
}) {
    return actionsVisible && canUpdate && !!spec?.canEdit;
}

/** Pure: delete button visibility. */
export function canShowDeleteButton({
    spec,
    canDelete,
    actionsVisible,
}: {
    spec: SpecificationResponse | undefined;
    canDelete: boolean;
    actionsVisible: boolean;
}) {
    return actionsVisible && canDelete && !!spec?.canDelete;
}

/** Pure: print button visibility. Hidden in the mobile webview and for general-job specs. */
export function canShowPrintButton({
    isMobileWebview,
    actionsVisible,
    isGeneralJob,
}: {
    isMobileWebview: boolean;
    actionsVisible: boolean;
    isGeneralJob: boolean;
}) {
    return actionsVisible && !isMobileWebview && !isGeneralJob;
}

/**
 * Pure: publish button visibility. Publish only shows for drafts OR specs with unpublished changes
 * (re-publish).
 */
export function canShowPublishButton({
    spec,
    canUpdate,
    actionsVisible,
    isDraft,
    isGeneralJob,
}: {
    spec: SpecificationResponse | undefined;
    canUpdate: boolean;
    actionsVisible: boolean;
    isDraft: boolean;
    isGeneralJob: boolean;
}) {
    return (
        actionsVisible &&
        canUpdate &&
        !isGeneralJob &&
        !!spec?.canEdit &&
        (isDraft || !!spec?.hasUnpublishedChanges)
    );
}

/** Pure: unpublish (move to draft) button visibility. Only shown on published specs the user can edit; never for general-job specs. */
export function canShowUnpublishButton({
    spec,
    canUpdate,
    actionsVisible,
    isPublished,
    isGeneralJob,
}: {
    spec: SpecificationResponse | undefined;
    canUpdate: boolean;
    actionsVisible: boolean;
    isPublished: boolean;
    isGeneralJob: boolean;
}) {
    return actionsVisible && canUpdate && isPublished && !!spec?.canEdit && !isGeneralJob;
}

/** Pure: viewing-permissions button visibility. Never shown for general-job specs. */
export function canShowViewingPermissionsButton({
    spec,
    actionsVisible,
    isPublished,
    isGeneralJob,
}: {
    spec: SpecificationResponse | undefined;
    actionsVisible: boolean;
    isPublished: boolean;
    isGeneralJob: boolean;
}) {
    return actionsVisible && !!spec?.canShare && isPublished && !isGeneralJob;
}

export { canShowSaveToCatalogButton };

/** Pure: version history button visibility. Available for builders only, in all non-external modes. */
export function canShowVersionHistoryButton({
    isBuilder,
    isExternalMode,
    isEditMode,
    isPanelMode,
}: {
    isBuilder: boolean;
    isExternalMode: boolean;
    isEditMode: boolean;
    isPanelMode: boolean;
}) {
    return isBuilder && !isExternalMode && !isEditMode && !isPanelMode;
}

/** Pure: unpublished-changes banner visibility. */
export function canShowUnpublishedChangesBanner({
    spec,
    isPublished,
    isBuilder,
    isExternalMode,
    isEditMode,
    selectedSnapshotId,
}: {
    spec: SpecificationResponse | undefined;
    isPublished: boolean;
    isBuilder: boolean;
    isExternalMode: boolean;
    isEditMode: boolean;
    selectedSnapshotId: string | null;
}) {
    return (
        isPublished &&
        !!spec?.hasUnpublishedChanges &&
        isBuilder &&
        !isExternalMode &&
        !isEditMode &&
        !selectedSnapshotId
    );
}

interface IUseSpecificationActionVisibilityParams {
    specification: SpecificationResponse | undefined;
    isExternalMode: boolean;
    isEditMode: boolean;
    isMobileWebview: boolean;
    isPanelMode: boolean;
    isBuilder: boolean;
    selectedSnapshotId: string | null;
    canUpdate: boolean;
    canDelete: boolean;
    canAdd: boolean;
    isGeneralJob: boolean;
    isGlobalSpecCatalogEnabled: boolean;
}

/**
 * Composes visibility flags for the SpecificationViewer's action buttons + banner. Delegates each
 * decision to a pure utility function so individual rules can be unit-tested and reused from other
 * components in isolation.
 */
export function useSpecificationActionVisibility({
    specification,
    isExternalMode,
    isEditMode,
    isMobileWebview,
    isPanelMode,
    isBuilder,
    selectedSnapshotId,
    canUpdate,
    canDelete,
    canAdd,
    isGeneralJob,
    isGlobalSpecCatalogEnabled,
}: IUseSpecificationActionVisibilityParams) {
    return useMemo(() => {
        const isDraft = specification?.status === SpecificationStatus.Draft;
        const isPublished = specification?.status === SpecificationStatus.Published;
        const actionsVisible = areActionsVisibleInMode({
            isExternalMode,
            isEditMode,
            selectedSnapshotId,
        });

        const showEdit = canShowEditButton({ spec: specification, canUpdate, actionsVisible });
        const showDelete = canShowDeleteButton({
            spec: specification,
            canDelete,
            actionsVisible,
        });
        const showPrint = canShowPrintButton({ isMobileWebview, actionsVisible, isGeneralJob });
        const showPublish = canShowPublishButton({
            spec: specification,
            canUpdate,
            actionsVisible,
            isDraft,
            isGeneralJob,
        });
        const showUnpublish = canShowUnpublishButton({
            spec: specification,
            canUpdate,
            actionsVisible,
            isPublished,
            isGeneralJob,
        });
        const showViewingPermissions = canShowViewingPermissionsButton({
            spec: specification,
            actionsVisible,
            isPublished,
            isGeneralJob,
        });
        const showSaveToCatalog = canShowSaveToCatalogButton({
            isBuilder,
            canAdd,
            actionsVisible,
            isGeneralJob,
            isGlobalSpecCatalogEnabled,
        });

        return {
            isDraft,
            isPublished,
            showEdit,
            showDelete,
            showPrint,
            showPublish,
            showUnpublish,
            showViewingPermissions,
            showSaveToCatalog,
            showActionsMenu:
                showDelete ||
                showPrint ||
                showUnpublish ||
                showViewingPermissions ||
                showPublish ||
                showSaveToCatalog,
            showVersionHistoryButton: canShowVersionHistoryButton({
                isBuilder,
                isExternalMode,
                isEditMode,
                isPanelMode,
            }),
            showUnpublishedChangesBanner: canShowUnpublishedChangesBanner({
                spec: specification,
                isPublished,
                isBuilder,
                isExternalMode,
                isEditMode,
                selectedSnapshotId,
            }),
        };
    }, [
        specification,
        isExternalMode,
        isEditMode,
        isMobileWebview,
        isPanelMode,
        isBuilder,
        isGeneralJob,
        isGlobalSpecCatalogEnabled,
        selectedSnapshotId,
        canUpdate,
        canDelete,
        canAdd,
    ]);
}
